import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteCompanyModal } from '../components/modals';
import { Toast } from '../components/common';
import { useDebounce } from '../hooks/useDebounce';
import { useToast } from '../hooks/useToast';
import { getArchivedCompanies, restoreCompany, permanentlyDeleteCompany } from '../services/company.service';
import { TABLE_HEADER_STYLE, TABLE_CELL_STYLE } from '../styles/tableStyles';
import type { Company } from '../../../shared/types';

export function ArchivedCompanies() {
  const navigate = useNavigate();
  const { toasts, showToast, dismissToast } = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [deleteModalCompany, setDeleteModalCompany] = useState<Company | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const loadArchivedCompanies = async () => {
    try {
      setLoading(true);
      const data = await getArchivedCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Error loading archived companies:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArchivedCompanies();
  }, []);

  const handleRestore = async (companyId: string) => {
    try {
      setRestoringId(companyId);
      await restoreCompany(companyId);
      await loadArchivedCompanies();
      setRestoringId(null);
    } catch (error) {
      console.error('Error restoring company:', error);
      showToast('Failed to restore company', 'error');
      setRestoringId(null);
    }
  };

  const handlePermanentDelete = async (companyId: string) => {
    await permanentlyDeleteCompany(companyId);
    await loadArchivedCompanies();
  };

  // Search filtering - server already filtered for archived companies
  const filteredCompanies = useMemo(() => {
    if (!debouncedSearchTerm) return companies;
    const searchLower = debouncedSearchTerm.toLowerCase();
    return companies.filter((company) =>
      company.name.toLowerCase().includes(searchLower) ||
      company.address?.toLowerCase().includes(searchLower)
    );
  }, [companies, debouncedSearchTerm]);

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading archived companies...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px' }}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/companies')}
        style={{
          marginBottom: '24px',
          padding: '8px 16px',
          backgroundColor: '#f3f4f6',
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          fontSize: '14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        ← Back to Active Companies
      </button>

      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px', color: '#111827' }}>
          Archived Companies
        </h1>
        <p style={{ fontSize: '16px', color: '#6b7280' }}>
          View and manage archived companies - restore or permanently delete
        </p>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '24px' }}>
        <input
          type="text"
          placeholder="Search archived companies..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '12px 16px',
            fontSize: '14px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            outline: 'none',
          }}
        />
      </div>

      {/* Warning Banner */}
      <div
        style={{
          padding: '16px',
          backgroundColor: '#fffbeb',
          border: '1px solid #fcd34d',
          borderRadius: '8px',
          marginBottom: '24px',
          fontSize: '14px',
          color: '#92400e',
        }}
      >
        ⚠️ <strong>Warning:</strong> Permanently deleting a company will remove all associated data including users,
        jobs, time entries, and messages. This action cannot be undone.
      </div>

      {/* Archived Companies List */}
      <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
        {filteredCompanies.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            {searchTerm ? 'No archived companies found matching your search.' : 'No archived companies.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={TABLE_HEADER_STYLE}>Company</th>
                <th style={TABLE_HEADER_STYLE}>Phone</th>
                <th style={TABLE_HEADER_STYLE}>Users</th>
                <th style={TABLE_HEADER_STYLE}>Archived</th>
                <th style={TABLE_HEADER_STYLE}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((company) => {
                return (
                  <tr
                    key={company.id}
                    style={{
                      borderBottom: '1px solid #e5e7eb',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <td style={TABLE_CELL_STYLE}>
                      <div style={{ fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
                        {company.name}
                      </div>
                      {company.address && (
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>
                          {company.address}
                        </div>
                      )}
                    </td>
                    <td style={TABLE_CELL_STYLE}>
                      <div style={{ color: '#111827' }}>{company.phone || '-'}</div>
                    </td>
                    <td style={TABLE_CELL_STYLE}>
                      <div style={{ color: '#111827' }}>
                        {company.currentUsers}/{company.maxUsers}
                      </div>
                    </td>
                    <td style={TABLE_CELL_STYLE}>
                      <div style={{ color: '#6b7280', fontSize: '14px' }}>
                        {company.archivedAt ? new Date(company.archivedAt).toLocaleDateString() : '-'}
                      </div>
                    </td>
                    <td style={TABLE_CELL_STYLE}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleRestore(company.id)}
                          disabled={restoringId === company.id}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#ecfdf5',
                            color: '#10b981',
                            border: '1px solid #10b981',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: restoringId === company.id ? 'not-allowed' : 'pointer',
                            opacity: restoringId === company.id ? 0.5 : 1,
                          }}
                        >
                          {restoringId === company.id ? 'Restoring...' : 'Restore'}
                        </button>
                        <button
                          onClick={() => setDeleteModalCompany(company)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#fef2f2',
                            color: '#dc2626',
                            border: '1px solid #dc2626',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '500',
                            cursor: 'pointer',
                          }}
                        >
                          Delete Forever
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Company Modal */}
      {deleteModalCompany && (
        <DeleteCompanyModal
          company={deleteModalCompany}
          onClose={() => setDeleteModalCompany(null)}
          onDelete={handlePermanentDelete}
        />
      )}
      <Toast toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}

